import * as R from 'ramda';
import { assign, createMachine } from 'xstate';

import { deleteAlerts, getAlerts } from 'App/services/AlertService';
import { dateTimeSlashesFormat } from 'App/services/DateService';
import { errorHandler, hasLength, localize } from 'App/utils';

const deleteSelectedAlerts = (context) => {
    const [toDelete, toKeep] = R.partition(R.prop('checked'), context.data);
    if (hasLength(toDelete)) {
        return deleteAlerts(R.map(R.prop('uuid'), toDelete)).then(() => ({
            data: toKeep,
            deletedCount: toDelete.length,
        }));
    }
    return Promise.resolve({ data: toKeep, deletedCount: 0 });
};

const formatAlerts = R.compose(
    R.map(
        R.compose(
            R.over(R.lensProp('createdOn'), R.partialRight(localize, [dateTimeSlashesFormat])),
            R.assoc('checked', false)
        )
    ),
    R.values
);

const sortAlerts = (alerts, sortKey, sortOrder) => {
    const direction = sortOrder === 'asc' ? 1 : -1;

    return [...alerts].sort((leftAlert, rightAlert) => {
        const leftSubject = `${leftAlert.subject || ''}`.trim();
        const rightSubject = `${rightAlert.subject || ''}`.trim();
        const leftHasSubject = leftSubject.length > 0;
        const rightHasSubject = rightSubject.length > 0;

        if (leftHasSubject !== rightHasSubject) {
            return leftHasSubject ? -1 : 1;
        }

        if (sortKey === 'subject') {
            const leftSubjectLowercase = leftSubject.toLocaleLowerCase();
            const rightSubjectLowercase = rightSubject.toLocaleLowerCase();

            const subjectComparison = leftSubjectLowercase.localeCompare(rightSubjectLowercase, undefined, {
                sensitivity: 'base',
            });

            if (subjectComparison !== 0) {
                return subjectComparison * direction;
            }

            return 0;
        }

        const dateComparison = new Date(leftAlert.createdOn).getTime() - new Date(rightAlert.createdOn).getTime();

        return sortKey === 'createdOn' ? dateComparison * direction : dateComparison;
    });
};

const fetchAlerts = async (context) => {
    const searchFilters = R.pathOr({}, ['search'], context);
    const cleanedSearchFilters = R.pickBy((value) => R.complement(R.isEmpty)(R.trim(`${value || ''}`)), searchFilters);
    const response = await getAlerts({
        page: context.page,
        pageSize: context.pageSize,
        ...(context.sortKey ? { sortKey: context.sortKey, sortOrder: context.sortOrder } : {}),
        ...cleanedSearchFilters,
    });
    const isPaginatedResponse = R.has('pagination', response) && R.has('data', response);
    const alertsMap = isPaginatedResponse ? response.data : response;
    const alerts = R.values(alertsMap || {});
    const alertsToFormat = isPaginatedResponse ? alerts : sortAlerts(alerts, context.sortKey, context.sortOrder);
    const formattedAlerts = formatAlerts(alertsToFormat);
    const total = isPaginatedResponse
        ? R.pathOr(alertsToFormat.length, ['pagination', 'total'], response)
        : alertsToFormat.length;
    const totalPages = isPaginatedResponse
        ? R.pathOr(Math.ceil(total / context.pageSize) || 0, ['pagination', 'totalPages'], response)
        : Math.ceil(total / context.pageSize) || 0;
    const page = isPaginatedResponse ? R.pathOr(context.page, ['pagination', 'page'], response) : context.page;
    const pageSize = isPaginatedResponse
        ? R.pathOr(context.pageSize, ['pagination', 'pageSize'], response)
        : context.pageSize;

    return {
        data: formattedAlerts,
        pagination: {
            page,
            pageSize,
            total,
            totalPages,
        },
    };
};

const States = Object.freeze({
    LOADING: 'LOADING',
    DELETING: 'DELETING',
    LOADED: 'LOADED',
    CONFIRM_DELETE: 'CONFIRM_DELETE',
    FAILED: 'FAILED',
    DETAILS_SHOWING: 'DETAILS_SHOWING',
    GET_DETAILS: 'GET_DETAILS',
});

const Events = Object.freeze({
    SUCCESS: 'SUCCESS',
    DELETE: 'DELETE',
    CANCEL: 'CANCEL',
    TOGGLE_ALL: 'TOGGLE_ALL',
    TOGGLE_ITEM: 'TOGGLE_ITEM',
    SHOW_DETAILS: 'SHOW_DETAILS',
    HIDE_DETAILS: 'HIDE_DETAILS',
    CONFIRM: 'CONFIRM',
    PAGE_CHANGE: 'PAGE_CHANGE',
    SORT_CHANGE: 'SORT_CHANGE',
    SEARCH_CHANGE: 'SEARCH_CHANGE',
    FAILURE: 'FAILURE',
});

/**
 * Create a collection of event matchers
 */
const stateMatchers = {
    isLoading: R.equals(States.LOADING),
    isDeleting: R.equals(States.DELETING),
    isConfirmDelete: R.equals(States.CONFIRM_DELETE),
    isDetailsShowing: R.equals(States.DETAILS_SHOWING),
    isPending: R.anyPass([R.equals(States.LOADING), R.equals(States.DELETING)]),
};

const financialAssistanceAlertsMachine = createMachine(
    {
        id: 'financialAssistanceAlerts',
        initial: States.LOADING,
        context: {
            data: [],
            isAllSelected: false,
            modalProgramId: null,
            page: 1,
            pageSize: 10,
            sortKey: '',
            sortOrder: 'asc',
            search: {},
            total: 0,
            totalPages: 0,
        },
        states: {
            [States.LOADING]: {
                on: {
                    [Events.PAGE_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setPage'],
                    },
                    [Events.SORT_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setSort'],
                    },
                    [Events.SEARCH_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setSearch'],
                    },
                },
                invoke: {
                    id: 'getAlerts',
                    src: fetchAlerts,
                    onDone: {
                        target: States.LOADED,
                        actions: ['setData', 'setPagination'],
                    },
                    onError: {
                        target: States.FAILED,
                        actions: ['handleError'],
                    },
                },
            },
            [States.LOADED]: {
                on: {
                    [Events.DELETE]: { target: States.CONFIRM_DELETE },
                    [Events.TOGGLE_ITEM]: { actions: ['toggleItem'] },
                    [Events.TOGGLE_ALL]: { actions: ['toggleAll'] },
                    [Events.SHOW_DETAILS]: {
                        target: States.DETAILS_SHOWING,
                        actions: ['showDetails'],
                    },
                    [Events.PAGE_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setPage'],
                    },
                    [Events.SORT_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setSort'],
                    },
                    [Events.SEARCH_CHANGE]: {
                        target: States.LOADING,
                        actions: ['setSearch'],
                    },
                },
            },
            [States.DETAILS_SHOWING]: {
                on: {
                    [Events.HIDE_DETAILS]: { target: States.LOADED, actions: ['hideDetails'] },
                },
            },
            [States.CONFIRM_DELETE]: {
                on: {
                    [Events.CONFIRM]: { target: States.DELETING, actions: ['setExternalOnDone'] },
                    [Events.CANCEL]: { target: States.LOADED },
                },
            },
            [States.DELETING]: {
                invoke: {
                    id: 'deleteAlerts',
                    src: deleteSelectedAlerts,
                    onDone: {
                        target: States.LOADED,
                        actions: ['setData', 'decrementTotal', 'callExternalOnDone', 'toggleOffAll'],
                    },
                    onError: {
                        target: States.LOADED,
                        actions: ['handleError'],
                    },
                },
            },
            [States.FAILED]: {
                type: 'final',
                actions: ['handleError'],
            },
        },
    },
    {
        actions: {
            toggleItem: assign((context, event) => {
                return R.compose(
                    R.over(R.lensProp('data'), R.over(R.lensPath([event.index, 'checked']), R.not)),
                    R.over(R.lensProp('isAllSelected'), R.F)
                )(context);
            }),
            setData: assign((context, event) => {
                return R.compose(R.set(R.lensProp('data'), R.pathOr([], ['data', 'data'], event)))(context);
            }),
            setPagination: assign((context, event) => {
                const pagination = R.pathOr({}, ['data', 'pagination'], event);
                return {
                    ...context,
                    page: R.propOr(context.page, 'page', pagination),
                    pageSize: R.propOr(context.pageSize, 'pageSize', pagination),
                    total: R.propOr(context.total, 'total', pagination),
                    totalPages: R.propOr(context.totalPages, 'totalPages', pagination),
                };
            }),
            toggleAll: assign((context) => {
                return R.compose(
                    R.chain(
                        R.compose(R.over(R.lensProp('data')), R.compose(R.map, R.set(R.lensProp('checked')))),
                        R.prop('isAllSelected')
                    ),
                    R.over(R.lensProp('isAllSelected'), R.not)
                )(context);
            }),
            showDetails: assign((context, event) => {
                return R.compose(R.set(R.lensProp('modalProgramId'), event.programId))(context);
            }),
            hideDetails: assign((context) => {
                return R.compose(R.set(R.lensProp('modalProgramId'), null))(context);
            }),
            setExternalOnDone: assign((context, event) => {
                return R.assoc('onDone', event.onDone, context);
            }),
            toggleOffAll: assign((context) => {
                return R.assoc('isAllSelected', false, context);
            }),
            setPage: assign((context, event) => {
                return {
                    ...context,
                    page: event.pageIndex,
                    pageSize: event.pageSize,
                };
            }),
            setSort: assign((context, event) => {
                return {
                    ...context,
                    page: 1,
                    sortKey: event.sortKey,
                    sortOrder: event.sortOrder,
                };
            }),
            setSearch: assign((context, event) => {
                return {
                    ...context,
                    page: 1,
                    search: event.search,
                };
            }),
            decrementTotal: assign((context, event) => {
                const deletedCount = R.pathOr(0, ['data', 'deletedCount'], event);
                return {
                    ...context,
                    total: Math.max(0, context.total - deletedCount),
                };
            }),
            callExternalOnDone: (context) => {
                if (context.onDone) {
                    context.onDone();
                }
            },
            handleError: (context, event) => {
                errorHandler(event.data);
            },
        },
    }
);

export { States, Events, stateMatchers };

export default financialAssistanceAlertsMachine;
